import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import type { WithdrawalNetwork } from '../types';
import { withdrawalApi, withdrawalNetworksApi } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';
import { useModal } from '../hooks/useModal';
import CustomModal from '../components/CustomModal';
import PageSkeleton from '../components/skeletons/PageSkeleton';
import { useTelegramTheme, getInputClassesLarge } from '../hooks/useTelegramTheme';

const Withdraw = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTelegramTheme();
  const { modal, showAlert, showConfirm, closeModal } = useModal();
  const [networks, setNetworks] = useState<WithdrawalNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [selectedNetworkId, setSelectedNetworkId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [environment] = useState<'testnet' | 'mainnet'>(
    (import.meta.env.VITE_ENVIRONMENT as 'testnet' | 'mainnet') || 'testnet'
  );

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      const networksData = await withdrawalNetworksApi.getNetworks(environment);
      
      setNetworks(networksData);
      
      // Auto-select first network
      if (networksData.length > 0 && !selectedNetworkId) {
        setSelectedNetworkId(networksData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedNetwork = networks.find(n => n.id === selectedNetworkId);

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      await showAlert(
        t('withdraw.pleaseEnterAmount'),
        t('common.error')
      );
      return;
    }

    if (!walletAddress.trim()) {
      await showAlert(
        t('withdraw.pleaseEnterWallet'),
        t('common.error')
      );
      return;
    }

    if (!selectedNetworkId) {
      await showAlert(
        t('withdraw.pleaseSelectNetwork'),
        t('common.error')
      );
      return;
    }

    if (!selectedNetwork) return;

    const withdrawAmount = parseFloat(amount);
    const totalAmount = withdrawAmount + selectedNetwork.fee;
    const availableBalance = user?.balance || 0;

    if (withdrawAmount < selectedNetwork.minWithdraw) {
      await showAlert(
        t('withdraw.amountTooLow'),
        t('common.error')
      );
      return;
    }

    if (withdrawAmount > selectedNetwork.maxWithdraw) {
      await showAlert(
        t('withdraw.invalidAmount'),
        t('common.error')
      );
      return;
    }

    if (totalAmount > availableBalance) {
      await showAlert(
        t('withdraw.insufficientBalance'),
        t('common.error')
      );
      return;
    }

    const confirmed = await showConfirm(
      `${t('withdraw.withdraw')} ${amount} ${selectedNetwork.tokenSymbol}\n${walletAddress.slice(0, 10)}...\n\n${t('withdraw.fee')}: ${selectedNetwork.fee} ${selectedNetwork.tokenSymbol}\n${t('withdraw.receiveAmount')}: ${(withdrawAmount - selectedNetwork.fee).toFixed(2)} ${selectedNetwork.tokenSymbol}`,
      {
        title: t('withdraw.confirmWithdrawal')
      }
    );

    if (confirmed && user) {
      setSubmitting(true);
      try {
        await withdrawalApi.createWithdrawal({
          amount: withdrawAmount,
          networkId: selectedNetworkId,
          walletAddress: walletAddress.trim()
        });
        
        // Update user balance
        const { updateUser } = useAuthStore.getState();
        const { userApi } = await import('../services/api');
        const updatedUser = await userApi.getProfile();
        updateUser({ 
          balance: updatedUser.balance || user.balance,
        });
        
        await showAlert(
          t('withdraw.withdrawalSubmitted'),
          t('common.success')
        );
        
        // Navigate to History page - Withdrawals tab
        navigate('/history');
      } catch (error: any) {
        await showAlert(
          error.message || t('withdraw.withdrawalFailed'),
          t('common.error')
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getReceiveAmount = () => {
    if (!amount || !selectedNetwork) return 0;
    const withdrawAmount = parseFloat(amount) || 0;
    return Math.max(0, withdrawAmount - selectedNetwork.fee);
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl mb-1 text-white font-bold">
          {t('withdraw.title')}
        </h1>
        <p className="text-tg-hint text-sm">
          {t('withdraw.subtitle')}
        </p>
      </div>

      {/* Available Balance Card */}
      <div className="bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-xl p-6 mb-6">
        <div className="text-sm text-tg-hint mb-2">
          {t('withdraw.availableBalance')}
        </div>
        <div className="text-3xl font-bold text-primary">
          {formatCurrency(user?.balance || 0)}
        </div>
      </div>

      {/* Withdrawal Form */}
      <div className="bg-tg-secondary rounded-xl p-6 mb-6 border border-gray-800">
        <h2 className="text-lg font-semibold text-white mb-4">
          {t('withdraw.title')}
        </h2>
        
        {/* Amount Input */}
        <div className="mb-4">
          <label className="block text-sm text-tg-hint mb-2">
            {t('withdraw.withdrawAmount')}
          </label>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('withdraw.enterAmount')}
              className={getInputClassesLarge(theme, 'rounded-lg')}
              min={selectedNetwork?.minWithdraw || 0}
              max={selectedNetwork?.maxWithdraw || 10000}
            />
            {selectedNetwork && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-tg-hint text-sm">
                {selectedNetwork.tokenSymbol}
              </div>
            )}
          </div>
          {selectedNetwork && (
            <div className="text-xs text-tg-hint mt-1">
              {`Min: ${selectedNetwork.minWithdraw} ${selectedNetwork.tokenSymbol} | Max: ${selectedNetwork.maxWithdraw} ${selectedNetwork.tokenSymbol}`}
            </div>
          )}
        </div>

        {/* Network Selection */}
        <div className="mb-4">
          <label className="block text-sm text-tg-hint mb-2">
            {t('withdraw.network')}
          </label>
          <select
            value={selectedNetworkId}
            onChange={(e) => setSelectedNetworkId(e.target.value)}
            className={getInputClassesLarge(theme, 'rounded-lg')}
          >
            <option value="">{t('withdraw.selectNetwork')}</option>
            {networks.map((net) => (
              <option key={net.id} value={net.id}>
                {net.name} 
                {/* ({net.environment === 'testnet' ? 'Testnet' : 'Mainnet'}) */}
              </option>
            ))}
          </select>
        </div>

        {/* Wallet Address */}
        <div className="mb-4">
          <label className="block text-sm text-tg-hint mb-2">
            {t('withdraw.walletAddress')}
          </label>
          <input
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder={t('withdraw.enterWalletAddress')}
            className={getInputClassesLarge(theme, 'rounded-lg')}
          />
        </div>

        {/* Fee and Receive Amount */}
        {amount && parseFloat(amount) > 0 && selectedNetwork && (
          <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-tg-hint">{t('withdraw.fee')}</span>
              <span className="text-sm font-semibold text-white">
                {selectedNetwork.fee} {selectedNetwork.tokenSymbol}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-primary/20">
              <span className="text-base font-semibold text-white">
                {t('withdraw.receiveAmount')}
              </span>
              <span className="text-xl font-bold text-primary">
                {getReceiveAmount().toFixed(2)} {selectedNetwork.tokenSymbol}
              </span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          className="w-full py-4 bg-gradient-to-r from-primary to-primary/90 text-black rounded-lg text-base font-semibold transition-opacity active:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={handleWithdraw}
          disabled={submitting || !selectedNetworkId || !amount || !walletAddress}
        >
          {submitting 
            ? t('withdraw.processing')
            : t('withdraw.withdraw')}
        </button>
      </div>

      {/* Custom Modal */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default Withdraw;

